import React, { FC } from 'react';

import { Box } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { InfinityIcon } from 'components/Icons';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

import { NamedListCell } from './NamedListCell';
import { TableSection } from './TableSection';
import { PermanentAccessInfo } from './types';

interface Props {
  data: PermanentAccessInfo[];
  isLoading?: boolean;
  tableStyles?: Record<string, any>;
  onOpenResource(resourceType, uuid: string, accountId: string): void;
}

// Users and Okta groups with permanent (non-JIT) access to the data group.
// Fed with an empty list until the backend permanent-access endpoint exists.
export const PermanentAccessSection: FC<Props> = props => {
  const { data, isLoading = false, tableStyles, onOpenResource } = props;
  const { accountMapping } = useCloudAccountId();
  const { getResourceAlias } = useResourceType();

  const columns = [
    {
      header: <Box pl="44px">Name</Box>,
      render: ({ row }: { row: PermanentAccessInfo }) => (
        <WithResourceIcon resourceType={row.identity_type}>
          {row.name}
        </WithResourceIcon>
      ),
      styles: { width: '22%' },
    },
    {
      header: 'Type',
      align: 'left',
      render: ({ row }: { row: PermanentAccessInfo }) =>
        getResourceAlias(row.identity_type) || '-',
      styles: { width: '18%' },
    },
    {
      header: 'Account',
      align: 'left',
      render: ({ row }: { row: PermanentAccessInfo }) => {
        const account = accountMapping?.[row.account_id];
        return account?.name ?? account?.cloud_account_id ?? row.account_id;
      },
      styles: { width: '24%' },
    },
    {
      header: 'Member Of',
      render: ({ row }: { row: PermanentAccessInfo }) => {
        const count = row.member_of?.length ?? 0;
        return (
          <NamedListCell
            items={row.member_of}
            header="Group Membership"
            label={`${count} group${count === 1 ? '' : 's'}`}
          />
        );
      },
      styles: { width: '18%', cell: { textAlign: 'center' } },
    },
    {
      header: 'Allowed Roles',
      render: ({ row }: { row: PermanentAccessInfo }) => (
        <NamedListCell
          items={row.roles}
          header="Allowed Roles"
          label={String(row.roles?.length ?? 0)}
        />
      ),
      styles: { width: '18%', cell: { textAlign: 'center' } },
    },
  ];

  return (
    <TableSection
      title="Permanent Access"
      count={data.length}
      icon={<InfinityIcon />}
      color={customTheme.colors.critical}
    >
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        noDataMessage="No permanent access"
        variant=""
        cursor="pointer"
        styles={tableStyles}
        onRowClick={(row: PermanentAccessInfo) =>
          onOpenResource(row.identity_type, row.uuid, row.account_id)
        }
      />
    </TableSection>
  );
};
